import React, { useState, useEffect, useMemo } from "react";
import { Offcanvas, Form, Button, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { Target, UsuarioMeta } from "./types";
import { UsuarioMetaDto, MetaRequest } from "../../../types/meta";
import { useUsuariosVendedores } from "../../../hooks/useMeta";
import { useToast } from "../../../providers/ToastProvider";

interface CadastroMetaOffCanvasProps {
  show: boolean;
  onHide: () => void;
  onSave: (meta: MetaRequest) => void; // Atualizado para usar MetaRequest da API
  editTarget?: Target;
}

const CadastroMetaOffCanvas: React.FC<CadastroMetaOffCanvasProps> = ({
  show,
  onHide,
  onSave,
  editTarget,
}) => {
  const [metaDescription, setMetaDescription] = useState("");
  const [metaStartDate, setMetaStartDate] = useState("");
  const [metaEndDate, setMetaEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userMetas, setUserMetas] = useState<{ [key: number]: number }>({});
  const [displayValues, setDisplayValues] = useState<{ [key: number]: string }>(
    {}
  );
  const { showToast } = useToast();

  // Buscar usuários
  const {
    data: users,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useUsuariosVendedores();

  // Filtrar usuários baseado na pesquisa
  const filteredUsers = useMemo(() => {
    if (!users) {
      return [];
    }
    const filtered = users.filter(
      (user: UsuarioMetaDto) =>
        user.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email &&
          user.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return filtered;
  }, [users, searchTerm]);

  // Calcular usuários com metas válidas
  const usersWithMetas = useMemo(() => {
    if (!users) return [];

    return users
      .map((user: UsuarioMetaDto) => ({
        ...user,
        valorMeta: userMetas[user.usuarioId] || 0,
      }))
      .filter((user) => user.valorMeta > 0);
  }, [users, userMetas]);

  // Reset ao fechar
  useEffect(() => {
    if (!show) {
      setMetaDescription("");
      setMetaStartDate("");
      setMetaEndDate("");
      setSearchTerm("");
      setUserMetas({});
      setDisplayValues({});
    }
  }, [show]);

  // Carregamento dos dados para edição
  useEffect(() => {
    if (show && editTarget) {
      setMetaDescription(editTarget.description || "");
      setMetaStartDate(editTarget.startDate);
      setMetaEndDate(editTarget.endDate);

      // Carregar valores das metas existentes
      const metas: { [key: number]: number } = {};
      const displays: { [key: number]: string } = {};

      if (editTarget.usuarios && editTarget.usuarios.length > 0) {
        // Meta com múltiplos usuários
        editTarget.usuarios.forEach((usuario) => {
          metas[usuario.usuarioId] = usuario.valorMeta;
          displays[usuario.usuarioId] = formatCurrency(usuario.valorMeta)
            .replace("R$", "")
            .trim();
        });
      } else {
        // Meta individual (compatibilidade)
        const userId = parseInt(editTarget.employeeId);
        metas[userId] = editTarget.targetValue;
        displays[userId] = formatCurrency(editTarget.targetValue)
          .replace("R$", "")
          .trim();
      }

      setUserMetas(metas);
      setDisplayValues(displays);
    }
  }, [show, editTarget]);

  const updateUserMeta = (userId: number, value: number) => {
    setUserMetas((prev) => ({
      ...prev,
      [userId]: value,
    }));
  };

  const updateDisplayValue = (userId: number, display: string) => {
    setDisplayValues((prev) => ({
      ...prev,
      [userId]: display,
    }));
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleMetaBlur = (userId: number) => {
    const currentValue = userMetas[userId] || 0;
    if (currentValue > 0) {
      // Formatar e atualizar o valor de display
      const formatted = formatCurrency(currentValue).replace("R$", "").trim();
      updateDisplayValue(userId, formatted);
    } else {
      // Limpar o display se não há valor
      updateDisplayValue(userId, "");
    }
  };

  const handleSave = () => {
    // Validações
    if (usersWithMetas.length === 0) {
      showToast(
        "Digite valores de meta para pelo menos um colaborador",
        "error"
      );
      return;
    }

    if (!metaStartDate || !metaEndDate) {
      showToast("Preencha as datas de início e fim", "error");
      return;
    }

    if (new Date(metaStartDate) >= new Date(metaEndDate)) {
      showToast("A data de início deve ser anterior à data de fim", "error");
      return;
    }

    // Converter para o formato da API
    const metaData: MetaRequest = {
      usuariosComMetas: usersWithMetas.map((user) => ({
        usuarioId: user.usuarioId,
        valorMeta: user.valorMeta,
      })),
      descricao: metaDescription,
      dataInicio: metaStartDate,
      dataFim: metaEndDate,
    };

    onSave(metaData);
  };

  //variavel para verificar se a data atual está dentro do periodo da meta
  const isCurrentDateInRange = useMemo(() => {
    if (!metaStartDate || !metaEndDate) return false;

    const currentDate = new Date();
    const start = new Date(metaStartDate);
    const end = new Date(metaEndDate);

    return currentDate >= start && currentDate <= end;
  }, [metaStartDate, metaEndDate]);

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" className="w-50">
      <Offcanvas.Header closeButton className="border-bottom">
        <Offcanvas.Title className="fw-bold">
          {editTarget ? "Editar Meta" : "Nova Meta"}
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="d-flex flex-column p-0">
        {/* Conteúdo principal com scroll */}
        <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
          {/* Descrição */}
          <div className="mb-3">
            <Form.Label className="fw-semibold">Descrição</Form.Label>
            <Form.Control
              type="text"
              placeholder="Digite uma descrição para a meta..."
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              className="form-control"
            />
          </div>

          {/* Período */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label className="fw-semibold">Data de Início</Form.Label>
              <Form.Control
                type="date"
                value={metaStartDate}
                onChange={(e) => setMetaStartDate(e.target.value)}
                className="form-control"
                disabled={editTarget && isCurrentDateInRange}
              />
              <Form.Text className="text-muted" style={{ fontSize: "10px" }}>
                {isCurrentDateInRange && editTarget
                  ? "Não é possível alterar a data de início durante o período da meta"
                  : ""}
              </Form.Text>
            </Col>
            <Col md={6}>
              <Form.Label className="fw-semibold">Data de Fim</Form.Label>
              <Form.Control
                type="date"
                value={metaEndDate}
                onChange={(e) => setMetaEndDate(e.target.value)}
                className="form-control"
                disabled={editTarget && isCurrentDateInRange}
              />
              <Form.Text className="text-muted" style={{ fontSize: "10px" }}>
                {isCurrentDateInRange && editTarget
                  ? "Não é possível alterar a data de fim durante o período da meta"
                  : ""}
              </Form.Text>
            </Col>
          </Row>

          {/* Colaboradores */}
          <div className="mb-4">
            <Form.Label className="fw-semibold mb-3">Colaboradores</Form.Label>

            {/* Busca */}
            <div className="mb-3">
              <div className="position-relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="position-absolute"
                  style={{
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#6c757d",
                    fontSize: "14px",
                  }}
                />
                <Form.Control
                  type="text"
                  placeholder="Buscar colaborador por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="ps-5 border shadow-sm"
                  style={{
                    fontSize: "14px",
                    borderRadius: "8px",
                  }}
                />
              </div>
            </div>

            {/* Lista de usuários */}
            <div
              className="border rounded-3 bg-body"
              style={{
                maxHeight: "250px",
                overflowY: "auto",
                minHeight: "120px",
              }}
            >
              {isLoadingUsers ? (
                <div className="p-4 text-center text-body-tertiary">
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  Carregando colaboradores...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-4 text-center text-body-tertiary">
                  <div className="mb-2">
                    <FontAwesomeIcon
                      icon={faSearch}
                      size="2x"
                      className="text-body-secondary"
                    />
                  </div>
                  {searchTerm
                    ? "Nenhum colaborador encontrado"
                    : "Nenhum colaborador disponível"}
                </div>
              ) : (
                filteredUsers.map((user: UsuarioMetaDto, index: number) => {
                  const currentValue = userMetas[user.usuarioId] || 0;
                  const hasValue = currentValue > 0;

                  return (
                    <div
                      key={user.usuarioId}
                      className={`d-flex align-items-center py-3 px-3 ${
                        index !== filteredUsers.length - 1
                          ? "border-bottom"
                          : ""
                      }`}
                      style={{
                        backgroundColor: hasValue
                          ? "rgba(var(--phoenix-primary-lighter-rgb), 0.3)"
                          : "transparent",
                        borderLeft: hasValue
                          ? "3px solid var(--phoenix-primary)"
                          : "3px solid transparent",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {/* Foto de perfil */}
                      <div className="me-3 flex-shrink-0">
                        {user.imageUrl ? (
                          <img
                            src={user.imageUrl}
                            alt={user.nome}
                            className="rounded-circle"
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                              border: hasValue
                                ? "2px solid var(--phoenix-primary)"
                                : "2px solid var(--phoenix-body-bg)",
                            }}
                          />
                        ) : (
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: "40px",
                              height: "40px",
                              background: hasValue
                                ? "var(--phoenix-primary)"
                                : "var(--phoenix-secondary)",
                              color: "white",
                              fontSize: "16px",
                              fontWeight: "600",
                              border: "2px solid var(--phoenix-body-bg)",
                            }}
                          >
                            {user.nome.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>

                      {/* Informações do usuário */}
                      <div className="flex-grow-1 min-width-0">
                        <div className="d-flex flex-column">
                          <div
                            className="fw-semibold text-body-emphasis"
                            style={{ fontSize: "15px" }}
                          >
                            {user.nome}
                          </div>
                          <small
                            className="text-body-tertiary"
                            style={{ fontSize: "13px" }}
                          >
                            {user.email || "Email não informado"}
                          </small>
                        </div>
                      </div>

                      {/* Valor da meta */}
                      <div className="me-3" style={{ minWidth: "140px" }}>
                        <Form.Control
                          type="text"
                          placeholder="R$ 0,00"
                          value={displayValues[user.usuarioId] || ""}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            updateDisplayValue(user.usuarioId, inputValue);

                            // Tratar tanto ponto quanto vírgula como separador decimal
                            // Primeiro, substituir ponto por vírgula para padronizar
                            let cleanValue = inputValue.replace(
                              /[^\d,\.]/g,
                              ""
                            );

                            // Se tiver ponto, tratar como vírgula decimal
                            if (cleanValue.includes(".")) {
                              cleanValue = cleanValue.replace(".", ",");
                            }

                            // Converter para número (substituindo vírgula por ponto para parseFloat)
                            const numericValue =
                              parseFloat(cleanValue.replace(",", ".")) || 0;
                            updateUserMeta(user.usuarioId, numericValue);
                          }}
                          onBlur={() => handleMetaBlur(user.usuarioId)}
                          data-user-id={user.usuarioId}
                          style={{
                            textAlign: "right",
                            fontSize: "14px",
                            borderRadius: "6px",
                            border: "1px solid var(--phoenix-border-color)",
                            backgroundColor: "var(--phoenix-body-bg)",
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Resumo dos selecionados */}
          {usersWithMetas.length > 0 && (
            <div className="mb-4">
              <h6 className="fw-semibold mb-2">Resumo das Metas</h6>
              <div className="bg-body p-3 rounded">
                <div className="d-flex justify-content-between mb-2">
                  <span>Total de colaboradores:</span>
                  <strong>{usersWithMetas.length}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Valor total das metas:</span>
                  <strong>
                    {new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(
                      usersWithMetas.reduce(
                        (sum, user) => sum + user.valorMeta,
                        0
                      )
                    )}
                  </strong>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Botões fixos na parte inferior */}
        <div className="border-top p-4 bg-body">
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={onHide} className="flex-fill">
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isLoading}
              className="flex-fill"
            >
              {isLoading ? "Salvando..." : "Salvar Meta"}
            </Button>
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default CadastroMetaOffCanvas;
